/*
 * Urli Utsav — delivery promise estimator
 * Computes the arrival window in the browser, never in Liquid: product pages are
 * cached by Shopify's CDN, so a Liquid-rendered date would be served stale.
 *
 * Two calendars, deliberately kept separate:
 *   dispatch  — Urli Utsav packs 7 days a week, holidays included, until the cutoff.
 *   transit   — carriers move Mon-Sat, holidays excluded.
 */
(function () {
  'use strict';

  var STORE_TZ = 'America/Los_Angeles';
  var STORAGE_KEY = 'uu_destination';

  /*
   * Everything outside the next-day radius. The next-day zone is NOT here: it cannot be
   * expressed as prefix ranges (954 holds Santa Rosa at 65mi and Fort Bragg at 153mi),
   * so assets/urli-geo.js owns that list and answers through window.UrliZones.
   * If that file ever fails to load, California simply falls through to WEST — a wider
   * promise, never a faster one.
   */
  var US_TIERS = [
    [900, 961, 'west'], [889, 898, 'west'],
    [970, 986, 'west'], [832, 838, 'west'], [850, 865, 'west'], [870, 884, 'west'],
    [800, 831, 'central'], [590, 599, 'central'], [570, 577, 'central'],
    [500, 528, 'central'], [530, 549, 'central'], [550, 567, 'central'],
    [600, 658, 'central'], [660, 681, 'central'], [683, 693, 'central'],
    [700, 729, 'central'], [730, 749, 'central'], [750, 799, 'central'],
    [400, 499, 'east'], [300, 399, 'east'], [200, 299, 'east'],
    [100, 199, 'east'], [10, 99, 'east'], [0, 5, 'east']
  ];

  /*
   * Default destination, before the shopper types anything.
   * Country comes from Shopify's own market detection (IP-derived, no third party).
   * Region comes from the browser's IANA timezone, which is the same granularity the
   * tiers use — so no IP lookup service, no API key, no rate limit, no latency, and no
   * visitor IP handed to anyone. Where a timezone straddles two tiers, take the SLOWER
   * one: a guess may be conservative, never optimistic.
   */
  var TZ_TIERS = {
    'America/Los_Angeles': 'west', 'America/Tijuana': 'west',
    'America/Phoenix': 'west', 'America/Boise': 'west',
    'America/Denver': 'central',        // spans Colorado (central) and the Mountain states
    'America/Chicago': 'central', 'America/Menominee': 'central',
    'America/Indiana/Knox': 'central', 'America/Indiana/Tell_City': 'central',
    'America/North_Dakota/Center': 'central', 'America/North_Dakota/New_Salem': 'central',
    'America/North_Dakota/Beulah': 'central',
    'America/New_York': 'east', 'America/Detroit': 'east',
    'America/Anchorage': 'offshore', 'America/Juneau': 'offshore', 'America/Nome': 'offshore',
    'America/Sitka': 'offshore', 'America/Yakutat': 'offshore', 'America/Adak': 'offshore',
    'America/Metlakatla': 'offshore', 'Pacific/Honolulu': 'offshore',
    'America/Puerto_Rico': 'offshore'
  };
  var TIER_NAMES = {
    west: 'the West Coast', central: 'the Central US',
    east: 'the East Coast', offshore: 'Alaska, Hawaii and Puerto Rico', canada: 'Canada'
  };

  /*
   * 3-digit ZIP prefix -> state, so a ZIP the shopper typed reads in the same vocabulary
   * as one we detected: "Deliver to Arizona", not "Deliver to United States, 85284".
   * Validated against 157 real ZIPs including every range boundary. The two exceptions
   * are Texas SCFs that sit inside the Oklahoma and New Mexico ranges.
   */
  var STATE_EXACT = { 733: 'Texas', 885: 'Texas' };
  var STATE_RANGES = [
    [5, 5, 'New York'], [6, 9, 'Puerto Rico'], [10, 27, 'Massachusetts'],
    [28, 29, 'Rhode Island'], [30, 38, 'New Hampshire'], [39, 49, 'Maine'],
    [50, 59, 'Vermont'], [60, 69, 'Connecticut'], [70, 89, 'New Jersey'],
    [100, 149, 'New York'], [150, 196, 'Pennsylvania'], [197, 199, 'Delaware'],
    [200, 205, 'District of Columbia'], [206, 219, 'Maryland'], [220, 246, 'Virginia'],
    [247, 268, 'West Virginia'], [270, 289, 'North Carolina'], [290, 299, 'South Carolina'],
    [300, 319, 'Georgia'], [320, 349, 'Florida'], [350, 369, 'Alabama'],
    [370, 385, 'Tennessee'], [386, 397, 'Mississippi'], [398, 399, 'Georgia'],
    [400, 427, 'Kentucky'], [430, 459, 'Ohio'], [460, 479, 'Indiana'],
    [480, 499, 'Michigan'], [500, 528, 'Iowa'], [530, 549, 'Wisconsin'],
    [550, 567, 'Minnesota'], [570, 577, 'South Dakota'], [580, 588, 'North Dakota'],
    [590, 599, 'Montana'], [600, 629, 'Illinois'], [630, 658, 'Missouri'],
    [660, 679, 'Kansas'], [680, 693, 'Nebraska'], [700, 714, 'Louisiana'],
    [716, 729, 'Arkansas'], [730, 749, 'Oklahoma'], [750, 799, 'Texas'],
    [800, 816, 'Colorado'], [820, 831, 'Wyoming'], [832, 838, 'Idaho'],
    [840, 847, 'Utah'], [850, 865, 'Arizona'], [870, 884, 'New Mexico'],
    [889, 898, 'Nevada'], [900, 961, 'California'], [967, 968, 'Hawaii'],
    [970, 979, 'Oregon'], [980, 994, 'Washington'], [995, 999, 'Alaska']
  ];

  function stateForZip(zip) {
    if (!/^\d{5}$/.test(zip)) return null;
    var p = parseInt(zip.slice(0, 3), 10);
    if (STATE_EXACT[p]) return STATE_EXACT[p];
    for (var i = 0; i < STATE_RANGES.length; i++) {
      if (p >= STATE_RANGES[i][0] && p <= STATE_RANGES[i][1]) return STATE_RANGES[i][2];
    }
    return null;
  }

  function safeTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
  }
  function shopifyCountry() {
    return (window.Shopify && window.Shopify.country) || '';
  }

  function guessTier() {
    var country = shopifyCountry();
    if (country === 'CA') return 'canada';
    if (country && country !== 'US') return null;   // outside the shipping zones
    var tz = safeTz();
    if (TZ_TIERS[tz]) return TZ_TIERS[tz];
    // Indiana and Kentucky have many sub-zones; all of them are East.
    if (tz.indexOf('America/Indiana/') === 0 || tz.indexOf('America/Kentucky/') === 0) return 'east';
    return null;
  }

  /* ---------- date helpers: integer day numbers, no Date-object timezone traps ---------- */
  function storeNow(cfg, now) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TZ, year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', hour12: false
    }).formatToParts(now);
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    return { y: +o.year, m: +o.month, d: +o.day, h: +(o.hour === '24' ? 0 : o.hour) };
  }
  function toDay(o) { return Math.floor(Date.UTC(o.y, o.m - 1, o.d) / 86400000); }
  function fromDay(n) {
    var t = new Date(n * 86400000);
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  }
  function isoKey(n) {
    var o = fromDay(n);
    return o.y + '-' + String(o.m).padStart(2, '0') + '-' + String(o.d).padStart(2, '0');
  }
  function dow(n) { return (n + 4) % 7; } // 1970-01-01 was a Thursday

  function tierFor(zip, country) {
    if (country === 'CA') return 'canada';
    if (country !== 'US' || !/^\d{5}$/.test(zip)) return 'unknown';
    if (window.UrliZones && window.UrliZones.isNextDay(zip)) return 'next_day';
    var p = parseInt(zip.slice(0, 3), 10);
    if (p >= 967 && p <= 968) return 'offshore';   // Hawaii
    if (p >= 995 && p <= 999) return 'offshore';   // Alaska
    if (p >= 6 && p <= 9) return 'offshore';       // Puerto Rico / VI
    for (var i = 0; i < US_TIERS.length; i++) {
      if (p >= US_TIERS[i][0] && p <= US_TIERS[i][1]) return US_TIERS[i][2];
    }
    return 'unknown';
  }

  function estimate(cfg, tier, now) {
    var s = storeNow(cfg, now || new Date());
    var today = toDay(s);
    var span = cfg.tiers[tier] || cfg.tiers.unknown;

    // Liquid's `split` filter yields strings, so coerce before comparing to a number.
    var shipDays = (cfg.shipDays || []).map(Number);
    var isHoliday = function (n) { return cfg.holidays.indexOf(isoKey(n)) > -1; };
    var isDispatchDay = function (n) { return shipDays.indexOf(dow(n)) > -1; };
    var isMoveDay = function (n) {
      var d = dow(n);
      if (d === 0 && !cfg.carrierSunday) return false;
      if (d === 6 && !cfg.carrierSaturday) return false;
      return !isHoliday(n);
    };
    var addTransit = function (n, days) {
      var x = n, left = days;
      while (left > 0) { x++; if (isMoveDay(x)) left--; }
      return x;
    };

    // Dispatch: before cutoff on a dispatch day -> today, else the next dispatch day.
    var ship;
    if (s.h < cfg.cutoffHour && isDispatchDay(today)) {
      ship = today;
    } else {
      ship = today + 1;
      var guard = 0;
      while (!isDispatchDay(ship) && guard < 14) { ship++; guard++; }
    }

    return {
      tier: tier, min: span[0], max: span[1],
      today: today, ship: ship, sameDay: ship === today,
      early: addTransit(ship, span[0]), late: addTransit(ship, span[1])
    };
  }

  function fmtDay(n) {
    var o = fromDay(n);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      .format(new Date(Date.UTC(o.y, o.m - 1, o.d)));
  }
  function fmtWindow(early, late) {
    if (early === late) return fmtDay(early);
    var a = fromDay(early), b = fromDay(late);
    return a.m === b.m ? fmtDay(early) + '–' + b.d : fmtDay(early) + ' – ' + fmtDay(late);
  }

  /* ---------- storage ---------- */
  function loadDest() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      return d && d.zip ? d : null;
    } catch (e) { return null; }
  }
  function saveDest(d) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch (e) { /* private mode */ }
  }

  /* ---------- component ---------- */
  function init(root) {
    if (root.dataset.uuReady === 'true') return;
    root.dataset.uuReady = 'true';

    var cfgEl = root.querySelector('[data-uu-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

    var etaEl = root.querySelector('[data-uu-eta]');
    var destEl = root.querySelector('[data-uu-dest-label]');
    var formEl = root.querySelector('[data-uu-form]');
    var toggleEl = root.querySelector('[data-uu-toggle]');
    var zipEl = root.querySelector('[data-uu-zip]');
    var ctryEl = root.querySelector('[data-uu-country]');
    var errEl = root.querySelector('[data-uu-error]');
    var pickupEl = root.querySelector('[data-uu-pickup]');

    var stored = loadDest();
    if (stored) {
      if (ctryEl) ctryEl.value = stored.country;
      if (zipEl) zipEl.value = stored.zip;
    }

    // Primed site-wide by assets/urli-geo.js, usually before this block ever runs.
    var geoData = (window.UrliGeo && window.UrliGeo.peek()) || null;

    /*
     * Destination resolution, most specific first. Each rung is a real downgrade in
     * precision but never in honesty: a coarser answer is wider, never wrong.
     *
     *   typed ZIP   -> "Deliver to the Bay Area" / "Deliver to California"
     *   IP lookup   -> "Estimated for the Bay Area" / "Estimated for Arizona"
     *   timezone    -> "Estimated for the West Coast"
     *   nothing     -> "Estimated for the USA", 1-7 days
     */
    function resolveDestination(d) {
      if (d.valid) {
        // Name the place, not the digits. Same vocabulary as a detected location, so the
        // line does not change character depending on how we came by the answer.
        var zipTier = tierFor(d.zip, d.country);
        var place;
        if (d.country === 'CA') place = 'Canada';
        else if (window.UrliZones && window.UrliZones.isBayArea(d.zip)) place = 'the Bay Area';
        else place = stateForZip(d.zip) || TIER_NAMES[zipTier] || 'the USA';
        return { tier: zipTier, label: 'Deliver to ' + place };
      }
      var tzTier = guessTier();
      if (geoData && geoData.country === 'US') {
        if (geoData.bay) return { tier: 'next_day', label: 'Estimated for the Bay Area' };
        if (geoData.state) {
          return { tier: geoData.tier || tzTier || 'unknown', label: 'Estimated for ' + geoData.state };
        }
      }
      if ((geoData && geoData.country === 'CA') || shopifyCountry() === 'CA') {
        return { tier: 'canada', label: 'Estimated for Canada' };
      }
      if (tzTier) return { tier: tzTier, label: 'Estimated for ' + TIER_NAMES[tzTier] };
      return { tier: 'unknown', label: 'Estimated for the USA' };
    }

    function variantInput() {
      return (cfg.formId && document.querySelector('#' + cfg.formId + ' .product-variant-id'))
        || document.querySelector('.product-variant-id');
    }

    function currentAvailability() {
      var input = variantInput();
      if (!input) return cfg.available;
      var v = cfg.variants[input.value];
      return typeof v === 'boolean' ? v : cfg.available;
    }

    function normalise() {
      var country = ctryEl ? ctryEl.value : 'US';
      var raw = zipEl ? zipEl.value.trim().toUpperCase() : '';
      var zip = country === 'US' ? raw.replace(/[^0-9]/g, '').slice(0, 5) : raw;
      var valid = country === 'CA' ? /^[A-Z]\d[A-Z]/.test(zip) : /^\d{5}$/.test(zip);
      return { country: country, zip: zip, valid: valid, empty: zip === '' };
    }

    function render(persist) {
      var d = normalise();
      var available = currentAvailability();

      if (errEl) {
        if (d.empty || d.valid) {
          errEl.hidden = true;
        } else {
          errEl.hidden = false;
          errEl.textContent = d.country === 'CA'
            ? 'Enter a postal code like M5V 2T6.'
            : 'Enter a 5-digit ZIP code.';
        }
      }
      if (persist && d.valid) saveDest({ country: d.country, zip: d.zip });

      var dest = resolveDestination(d);
      var e = estimate(cfg, dest.tier);

      if (etaEl) {
        if (!available) {
          etaEl.innerHTML = 'Sold out — <a href="' + cfg.contactUrl + '">message us</a> about a custom order';
        } else if (e.tier === 'unknown') {
          etaEl.innerHTML = 'Free shipping — <span class="uu-dp__date">arrives in '
            + cfg.tiers.unknown[0] + '–' + cfg.tiers.unknown[1] + ' days</span>';
        } else {
          var win = '<span class="uu-dp__date">' + fmtWindow(e.early, e.late) + '</span>';
          etaEl.innerHTML = e.sameDay
            ? 'Order today to get by ' + win + ' <span class="uu-dp__pill">Ships today</span>'
            : 'Ships ' + fmtDay(e.ship) + ', arrives by ' + win;
        }
      }

      if (destEl) destEl.textContent = dest.label;
      // The control names what it will do: refine a guess, or change a known address.
      if (toggleEl) toggleEl.textContent = d.valid ? 'Change' : 'Add ZIP';
      if (pickupEl) pickupEl.hidden = !(cfg.showPickup && e.tier === 'next_day');
    }

    if (toggleEl && formEl) {
      toggleEl.addEventListener('click', function () {
        var open = formEl.hasAttribute('hidden');
        if (open) { formEl.removeAttribute('hidden'); } else { formEl.setAttribute('hidden', ''); }
        toggleEl.setAttribute('aria-expanded', String(open));
      });
    }
    var applyEl = root.querySelector('[data-uu-apply]');
    if (applyEl) applyEl.addEventListener('click', function () { render(true); });
    if (zipEl) {
      zipEl.addEventListener('change', function () { render(true); });
      zipEl.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); render(true); }
      });
    }
    if (ctryEl) ctryEl.addEventListener('change', function () { render(true); });

    // Re-render when the selected variant changes: Dawn rewrites .product-variant-id.
    var vInput = variantInput();
    if (vInput && window.MutationObserver) {
      new MutationObserver(function () { render(false); })
        .observe(vInput, { attributes: true, attributeFilter: ['value'] });
    }
    document.addEventListener('variant:change', function () { render(false); });

    render(false);

    /*
     * Normally the primer has already answered by now and the paint above is final.
     * The exception is a visitor landing straight on a product page: the lookup is
     * still in flight, so wait on the same promise and repaint once it lands.
     */
    if (!geoData && window.UrliGeo && !normalise().valid) {
      window.UrliGeo.ensure().then(function (g) {
        if (!g) return;
        geoData = g;
        render(false);
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-uu-delivery-promise]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
